import { useState, useEffect } from "react";
import { apiClient } from "../App";
import { PlanLimitDialog, usePlanLimitHandler } from "../components/PlanUsage";
import Layout from "../components/Layout";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Switch } from "../components/ui/switch";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogTrigger,
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { 
  Plus, 
  Edit2,
  Trash2,
  Briefcase
} from "lucide-react";
import { toast } from "sonner";

const PayGroups = () => {
  const [payGroups, setPayGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [editingGroup, setEditingGroup] = useState(null);
  const [saving, setSaving] = useState(false);
  const [deleteDialog, setDeleteDialog] = useState({ open: false, id: null, name: "" });

  const [formData, setFormData] = useState({
    group_name: "",
    pay_frequency: "weekly",
    time_approval_required: false,
    hours_per_workday: 10
  });

  useEffect(() => {
    fetchPayGroups();
  }, []);

  const fetchPayGroups = async () => {
    try {
      const response = await apiClient.get('/pay-groups');
      setPayGroups(response.data.pay_groups);
    } catch (error) {
      console.error("Failed to fetch pay groups:", error);
    } finally {
      setLoading(false);
    }
  };

  const payGroupLimit = usePlanLimitHandler("pay_group");

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    const payload = {
      ...formData,
      hours_per_workday: formData.hours_per_workday === "" || formData.hours_per_workday == null
        ? 10
        : Number(formData.hours_per_workday)
    };
    const outcome = await payGroupLimit.run(() =>
      editingGroup
        ? apiClient.put(`/pay-groups/${editingGroup.pay_group_id}`, payload)
        : apiClient.post('/pay-groups', payload)
    );
    if (outcome.ok) {
      setShowAddDialog(false);
      setEditingGroup(null);
      setFormData({
        group_name: "",
        pay_frequency: "weekly",
        time_approval_required: false,
        hours_per_workday: 10
      });
      fetchPayGroups();
    } else if (outcome.blocked) {
      setShowAddDialog(false);
    } else {
      console.error("Failed to save pay group:", outcome.error);
    }
    setSaving(false);
  };

  const handleEdit = (group) => {
    setEditingGroup(group);
    setFormData({
      group_name: group.group_name,
      pay_frequency: group.pay_frequency,
      time_approval_required: group.time_approval_required,
      hours_per_workday: group.hours_per_workday ?? 10
    });
    setShowAddDialog(true);
  };

  const handleDelete = async (groupId) => {
    try {
      await apiClient.delete(`/pay-groups/${groupId}`);
      fetchPayGroups();
      setDeleteDialog({ open: false, id: null, name: "" });
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to delete pay group");
    }
  };

  const getFrequencyLabel = (freq) => {
    const labels = {
      weekly: "Weekly",
      semi_monthly: "Semi-Monthly (1-15, 16-End)",
      monthly: "Monthly"
    };
    return labels[freq] || freq;
  };

  return (
    <Layout>
      <div data-testid="paygroups-page" className="space-y-6">
        {/* Page Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold text-[#0A192F] font-['Manrope'] tracking-tight">
              Pay Groups
            </h1>
            <p className="text-slate-600 mt-1">
              Configure pay frequencies and approval requirements
            </p>
          </div>
          <Dialog open={showAddDialog} onOpenChange={(open) => {
            setShowAddDialog(open);
            if (!open) {
              setEditingGroup(null);
              setFormData({
                group_name: "",
                pay_frequency: "weekly",
                time_approval_required: false,
                hours_per_workday: 10
              });
            }
          }}>
            <DialogTrigger asChild>
              <Button 
                className="bg-[#0D9488] hover:bg-[#14B8A6]"
                data-testid="add-paygroup-btn"
              >
                <Plus className="w-4 h-4 mr-2" />
                Add Pay Group
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-md">
              <DialogHeader>
                <DialogTitle>{editingGroup ? "Edit Pay Group" : "Add Pay Group"}</DialogTitle>
                <DialogDescription>
                  {editingGroup ? "Update the pay group settings." : "Create a new pay group for your employees."}
                </DialogDescription>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="group_name">Group Name *</Label>
                  <Input
                    id="group_name"
                    value={formData.group_name}
                    onChange={(e) => setFormData({...formData, group_name: e.target.value})}
                    placeholder="e.g., Kitchen Staff"
                    required
                    data-testid="paygroup-name-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="pay_frequency">Pay Frequency *</Label>
                  <Select 
                    value={formData.pay_frequency} 
                    onValueChange={(val) => setFormData({...formData, pay_frequency: val})}
                  >
                    <SelectTrigger data-testid="paygroup-frequency-select">
                      <SelectValue placeholder="Select frequency" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="weekly">Weekly</SelectItem>
                      <SelectItem value="semi_monthly">Semi-Monthly (1-15, 16-End)</SelectItem>
                      <SelectItem value="monthly">Monthly</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="hours_per_workday">Hours per Workday</Label>
                  <Input
                    id="hours_per_workday"
                    type="number"
                    min={1}
                    max={24}
                    value={formData.hours_per_workday}
                    onChange={(e) => setFormData({...formData, hours_per_workday: e.target.value === "" ? "" : Number(e.target.value)})}
                    placeholder="10"
                    data-testid="paygroup-hours-per-workday-input"
                  />
                  <p className="text-xs text-slate-500">Used by the deduction calculator to convert days/half-days into hours (default: 10).</p>
                </div>
                <div className="flex items-center justify-between">
                  <div className="space-y-0.5">
                    <Label>Time Approval Required</Label>
                    <p className="text-sm text-slate-500">Require manager approval for time entries</p>
                  </div>
                  <Switch
                    checked={formData.time_approval_required}
                    onCheckedChange={(checked) => setFormData({...formData, time_approval_required: checked})}
                    data-testid="paygroup-approval-switch"
                  />
                </div>
                <div className="flex justify-end gap-2 pt-4">
                  <Button type="button" variant="outline" onClick={() => setShowAddDialog(false)}>
                    Cancel
                  </Button>
                  <Button 
                    type="submit" 
                    disabled={saving}
                    className="bg-[#0D9488] hover:bg-[#14B8A6]"
                    data-testid="save-paygroup-btn"
                  >
                    {saving ? "Saving..." : editingGroup ? "Update" : "Create"}
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        {/* Pay Groups List */}
        <Card className="border border-slate-200">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="bg-slate-50 hover:bg-slate-50">
                  <TableHead className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Group Name</TableHead>
                  <TableHead className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Pay Frequency</TableHead>
                  <TableHead className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Hrs/Day</TableHead>
                  <TableHead className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Time Approval</TableHead>
                  <TableHead className="text-xs uppercase tracking-wider text-slate-500 font-semibold text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading ? (
                  [...Array(3)].map((_, i) => (
                    <TableRow key={i}>
                      <TableCell colSpan={5}>
                        <div className="h-12 bg-slate-100 rounded animate-pulse"></div>
                      </TableCell>
                    </TableRow>
                  ))
                ) : payGroups.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={5} className="text-center py-12">
                      <Briefcase className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                      <p className="text-slate-500">No pay groups yet. Create one to organize your employees.</p>
                    </TableCell>
                  </TableRow>
                ) : (
                  payGroups.map((group) => (
                    <TableRow key={group.pay_group_id} data-testid="paygroup-row">
                      <TableCell className="font-medium text-[#0A192F]">
                        {group.group_name}
                      </TableCell>
                      <TableCell className="text-slate-600">
                        {getFrequencyLabel(group.pay_frequency)}
                      </TableCell>
                      <TableCell className="text-slate-600" data-testid="paygroup-hours-per-workday-cell">
                        {group.hours_per_workday ?? 10}
                      </TableCell>
                      <TableCell>
                        <Badge variant={group.time_approval_required ? "default" : "secondary"}>
                          {group.time_approval_required ? "Required" : "Not Required"}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-2">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleEdit(group)}
                            data-testid="edit-paygroup-btn"
                          >
                            <Edit2 className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => setDeleteDialog({ open: true, id: group.pay_group_id, name: group.group_name })}
                            className="text-[#E11D48] hover:text-[#E11D48] hover:bg-red-50"
                            data-testid="delete-paygroup-btn"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </Card>
      </div>
      <Dialog open={deleteDialog.open} onOpenChange={(open) => !open && setDeleteDialog({ open: false, id: null, name: "" })}>
        <DialogContent className="sm:max-w-sm">
          <DialogHeader>
            <DialogTitle className="text-[#0A192F]">Delete Pay Group?</DialogTitle>
            <DialogDescription className="pt-2">Are you sure you want to delete <span className="font-semibold">{deleteDialog.name}</span>? This cannot be undone.</DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteDialog({ open: false, id: null, name: "" })}>Cancel</Button>
            <Button className="bg-[#E11D48] hover:bg-[#BE123C] text-white" onClick={() => handleDelete(deleteDialog.id)}>Delete</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <PlanLimitDialog {...payGroupLimit.dialogProps} />
    </Layout>
  );
};

export default PayGroups;
